import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { ApiError } from '@/types';
import { devLog } from '@/utils/logger';
import i18n from '@/i18n';
import { requestAuthResync } from '@/services/authSync';
import { toast } from '@/utils/toast';

import { isLockedResponseBody, useGatingStore } from '@/stores/gating.store';
import {
  LAST_REVIEWER_ERROR_CODE,
  LastReviewerCancelledError,
  requestLastReviewerConfirm,
  type LastReviewerBrand,
} from '@/stores/lastReviewerConfirm.store';

/** Backend code (403) for brand-settings writes by reviewer-only people */
const BRAND_SETTINGS_DENIED_CODE = 'release.reviewer_no_brand_settings';
/** Backend code (403) for channel disconnect / delete / rename by reviewer-only people */
const CHANNEL_MANAGE_DENIED_CODE = 'release.reviewer_no_channel_manage';
/** Backend code (403) for variation creation by reviewer-only people */
const NOT_CREATOR_CODE = 'release.not_creator';
/** Backend code (403) when the plan does not include a feature (lifetime gating, enforce) */
const FEATURE_NOT_AVAILABLE_CODE = 'FEATURE_NOT_AVAILABLE';
/** Registry keys of the Brand Portfolio / release surfaces (decided on the agency package) */
const PORTFOLIO_FEATURE_PATTERN = /^(portfolio_|release_|brand_portfolio$)/;
/** Backend code (403) when a monthly package quota (e.g. blog posts) is used up */
const QUOTA_EXCEEDED_CODE = 'QUOTA_EXCEEDED';
/** Backend code (403) when a fixed package limit is reached (channels, variations, seats, Brand World profiles) */
const LIMIT_EXCEEDED_CODE = 'LIMIT_EXCEEDED';
/** The only LIMIT_EXCEEDED resource that opens the global upsell card; the others keep their own UX */
const BRAND_PROFILES_LIMIT_RESOURCE = 'brand_profiles';
/** Backend code (403) when the package does not include the platform being connected */
const PLATFORM_NOT_AVAILABLE_CODE = 'PLATFORM_NOT_AVAILABLE';
/** Backend code (403) when the AI token balance cannot cover the call */
const INSUFFICIENT_AI_TOKENS_CODE = 'INSUFFICIENT_AI_TOKENS';
/**
 * AI paths that had NO token handling of their own before the backend pre-check (trends, Lara insights,
 * auto-translate). Others (composer AI, blog-to-social) keep their existing token UI, so no second modal there.
 */
const GATING_HANDLED_KEY = '__gatingHandled';
interface GatingHandledMarker {
  [GATING_HANDLED_KEY]?: boolean;
}
/** Flags the error object so callers can tell a global gating surface already explained it. */
const markGatingHandled = (error: unknown): void => {
  if (typeof error === 'object' && error !== null) {
    (error as GatingHandledMarker)[GATING_HANDLED_KEY] = true;
  }
};

/** Opens the upsell and marks the error handled ONLY when a surface is actually visible (not suppressed). */
const openUpsellMarked = (
  error: unknown,
  request: Parameters<ReturnType<typeof useGatingStore.getState>['openUpsell']>[0],
  force?: boolean
): void => {
  if (useGatingStore.getState().openUpsell(request, force)) markGatingHandled(error);
};

const TOKEN_MODAL_URL_PATTERNS: readonly RegExp[] = [
  /\/api\/dashboard\/trends/,
  // insights/generate is excluded: AIInsightsWidget shows its own error toast for it (one surface, not two)
  /\/api\/analytics\/insights(?!\/generate)/,
  /\/variations\/auto/,
];

/**
 * API Client Configuration
 *
 * This module provides a configured axios instance with:
 * - Request interceptors to add auth tokens
 * - Response interceptors to handle token refresh
 * - Automatic retry on 401 errors
 * - Smart error handling that stops retries on non-retryable errors
 */

// Empty string means relative URLs (set in production .env);
// undefined (env var not set) falls back to localhost for dev.
const RAW = import.meta.env.VITE_API_URL;
const API_BASE_URL = RAW !== undefined ? RAW : 'http://localhost:3000';

/**
 * SECURITY: In-memory token storage ONLY (no localStorage)
 *
 * Access tokens are stored ONLY in memory to prevent XSS attacks.
 * Refresh tokens are stored in httpOnly cookies on the backend (not accessible to JavaScript).
 *
 * Trade-off: Access token is lost on page refresh, but the refresh token cookie
 * will automatically get a new access token on the first API call after refresh.
 */
let accessToken: string | null = null;

export const getAccessToken = (): string | null => accessToken;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
  if (token) {
    devLog.log('[API] Access token set in memory (secure, XSS-resistant)');
    // A genuinely fresh token (login, register, loginWithResponse, or a
    // successful refresh) proves the session is healthy again. Reset the
    // refresh circuit breaker here — the single choke point all of those
    // paths funnel through — so a breaker opened in a prior session/tab
    // doesn't leave `connectionDegraded` stuck true on a brand-new login
    // that never happened to call refreshAccessToken(). closeRefreshBreaker()
    // is a no-op (no listener notification) when the breaker wasn't open.
    closeRefreshBreaker();
  } else {
    devLog.log('[API] Access token cleared from memory');
  }
};

export const clearAccessToken = (): void => {
  accessToken = null;
  devLog.log('[API] Access token cleared from memory');
  // Hygiene: a logged-out state should never carry a stale degraded flag
  // into the next login.
  closeRefreshBreaker();
};

// ============================================================================
// ERROR CLASSIFICATION
// Determines if an error should trigger retries
// ============================================================================

/**
 * Errors that should NOT be retried
 * - 4xx client errors (except 429 rate limit)
 * - Network errors that indicate permanent issues
 */
export const isNonRetryableError = (error: AxiosError): boolean => {
  // No response = network error, could be transient
  if (!error.response) {
    // But if it's a timeout or abort, don't retry
    if (error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED') {
      return true;
    }
    return false; // Network error might be transient
  }

  const status = error.response.status;

  // 429 Too Many Requests - should retry after cooldown
  if (status === 429) return false;

  // 401 Unauthorized - handled by axios interceptor (token refresh), allow retry
  if (status === 401) return false;

  // Other 4xx client errors - don't retry (bad request, forbidden, not found, etc.)
  if (status >= 400 && status < 500) return true;

  // 5xx server errors - might be transient, allow retry
  return false;
};

/**
 * Check if error is a rate limit error
 */
export const isRateLimitError = (error: AxiosError): boolean => {
  return error.response?.status === 429;
};

/**
 * Determine whether an error represents a GENUINE auth rejection
 * (the server actually responded 401/403), as opposed to a transient
 * network error or timeout (no `.response` at all).
 *
 * This distinction matters for token refresh failures: a refresh call that
 * never reached the server (flaky connection, timeout) does NOT mean the
 * user's session is invalid — it means we couldn't verify it right now.
 * Only a real 401/403 from the refresh endpoint means the refresh cookie
 * was rejected and the user should be logged out.
 */
export const isAuthRejection = (error: unknown): boolean => {
  return axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403);
};

/**
 * Thrown when a token-refresh call returns a 2xx response but the body is
 * missing `data.accessToken`. This used to be a raw destructuring
 * TypeError; making it a typed, named error keeps the failure classifiable
 * (non-auth → counts toward the refresh circuit breaker below) instead of
 * surfacing as an opaque crash.
 */
export class TokenRefreshResponseError extends Error {
  constructor(message = 'Token refresh response did not include an access token') {
    super(message);
    this.name = 'TokenRefreshResponseError';
  }
}

/**
 * Thrown by the response interceptor when it short-circuits a 401 because
 * the refresh circuit breaker (see below) is currently open. Distinct from
 * a real refresh failure so callers/logs can tell "we didn't even try" from
 * "we tried and the server rejected us".
 */
export class RefreshBreakerOpenError extends Error {
  constructor(message = 'Token refresh temporarily suspended after repeated failures') {
    super(message);
    this.name = 'RefreshBreakerOpenError';
  }
}

/**
 * Get retry-after time from error response (in ms)
 */
export const getRetryAfterMs = (error: AxiosError): number => {
  const retryAfter = error.response?.headers?.['retry-after'];
  if (retryAfter) {
    const seconds = parseInt(retryAfter, 10);
    if (!isNaN(seconds)) return seconds * 1000;
  }
  // Default: 60 seconds for rate limit errors
  return isRateLimitError(error) ? 60000 : 1000;
};

/**
 * Create axios instance
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies (for refresh token)
});

/**
 * Request Interceptor: Auth & Language
 * Add access token and Accept-Language header to requests
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Add Accept-Language header for i18n error messages
    config.headers['Accept-Language'] = i18n.language || 'en';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handle 401 errors and token refresh
 *
 * Implements:
 * - Token refresh on 401 errors with mutex/lock pattern
 * - Request queuing to prevent duplicate refresh calls
 * - Automatic retry of failed requests after refresh
 * - Timeout protection to prevent hanging refresh requests
 */

// Token refresh timeout (10 seconds)
const REFRESH_TIMEOUT = 10000;

/**
 * Token Refresh Lock
 * Ensures only one refresh happens at a time using a mutex pattern
 */
class TokenRefreshLock {
  private refreshPromise: Promise<string> | null = null;

  /**
   * Refresh token with lock to prevent race conditions
   * If already refreshing, waits for the existing promise
   */
  async refreshWithLock(refreshFn: () => Promise<string>): Promise<string> {
    // If already refreshing, wait for the existing promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Create new refresh promise
    this.refreshPromise = refreshFn().finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }
}

const tokenRefreshLock = new TokenRefreshLock();

/**
 * Refresh Circuit Breaker
 *
 * Bug B (2026-09): commit 527e361 made the interceptor "preserve session and
 * reject" on any non-401/403 refresh failure (good intent — don't log out on
 * a flaky connection) but added NO cap. React Query then re-fires the failed
 * query, which 401s again, which refreshes again, forever — an unbounded,
 * completely silent (mid-session) live-lock that hammers /api/auth/refresh
 * until the tab is closed or the user manually re-logs-in.
 *
 * This breaker tracks CONSECUTIVE non-auth refresh failures (genuine
 * 401/403 rejections are NOT counted — those are a definitive outcome that
 * already ends in logout, see the interceptor below). After
 * REFRESH_FAILURE_THRESHOLD consecutive non-auth failures, the breaker OPENS
 * for REFRESH_BREAKER_COOLDOWN_MS: during that window the 401 interceptor
 * short-circuits (rejects immediately) instead of calling /auth/refresh
 * again, so we stop hammering both the refresh endpoint and (indirectly,
 * since callers stop getting a fresh token to retry with) the original
 * resource endpoint. Once the cooldown elapses, the NEXT 401 is allowed one
 * probe refresh: success closes the breaker (counter resets), failure
 * re-opens it (extends the cooldown).
 */
const REFRESH_FAILURE_THRESHOLD = 3;
const REFRESH_BREAKER_COOLDOWN_MS = 15000;

let consecutiveNonAuthRefreshFailures = 0;
// Epoch ms until which the breaker is open; 0 (or a past timestamp) = closed.
let refreshBreakerOpenUntil = 0;

type RefreshBreakerListener = (isOpen: boolean) => void;
const refreshBreakerListeners = new Set<RefreshBreakerListener>();

/**
 * Subscribe to refresh circuit breaker state changes so React (AuthContext)
 * can surface a non-destructive mid-session "reconnecting" indicator without
 * api.ts needing to import React. Returns an unsubscribe function.
 */
export const subscribeToRefreshBreaker = (listener: RefreshBreakerListener): (() => void) => {
  refreshBreakerListeners.add(listener);
  return () => {
    refreshBreakerListeners.delete(listener);
  };
};

const notifyRefreshBreakerListeners = (isOpen: boolean): void => {
  refreshBreakerListeners.forEach((listener) => listener(isOpen));
};

/** True while the breaker is open (cooldown window not yet elapsed). */
const isRefreshBreakerOpen = (): boolean => Date.now() < refreshBreakerOpenUntil;

const openRefreshBreaker = (): void => {
  const wasOpen = isRefreshBreakerOpen();
  refreshBreakerOpenUntil = Date.now() + REFRESH_BREAKER_COOLDOWN_MS;
  if (!wasOpen) {
    devLog.warn(
      `[API] Refresh circuit breaker OPEN after ${consecutiveNonAuthRefreshFailures} consecutive non-auth failures — cooling down ${REFRESH_BREAKER_COOLDOWN_MS}ms`
    );
    notifyRefreshBreakerListeners(true);
  }
};

const closeRefreshBreaker = (): void => {
  const wasOpen = refreshBreakerOpenUntil > 0;
  consecutiveNonAuthRefreshFailures = 0;
  refreshBreakerOpenUntil = 0;
  if (wasOpen) {
    devLog.log('[API] Refresh circuit breaker CLOSED (refresh succeeded)');
    notifyRefreshBreakerListeners(false);
  }
};

const REFRESH_LOCK_NAME = 'auth-token-refresh';

/**
 * Cross-context (cross-tab / PWA + browser tab) refresh serialization via the
 * Web Locks API. The refresh cookie is shared across contexts and the backend
 * rotates it single-use, so concurrent refreshes from two tabs race and the
 * loser is logged out. Serializing makes each context send the CURRENT cookie.
 * The lock is released automatically when the callback promise settles
 * (resolve or reject), so a failed refresh cannot deadlock other contexts;
 * the callback's own request is bounded by REFRESH_TIMEOUT. Falls back to
 * running directly (per-context mutex only) when Web Locks is unavailable.
 *
 * Lock ACQUISITION is bounded by REFRESH_TIMEOUT + 2000ms: if another context
 * froze while holding the lock (iOS PWA / bfcache suspend mid-refresh) we stop
 * waiting and refresh without the cross-context lock. Any lock-acquisition
 * error (AbortError, SecurityError, ...) also falls through to a direct run.
 * Only errors thrown by `fn` itself propagate (and are the only ones that feed
 * the refresh circuit breaker); lock errors never do.
 */
const REFRESH_LOCK_ACQUIRE_TIMEOUT_MS = REFRESH_TIMEOUT + 2000;

const withCrossContextRefreshLock = async (fn: () => Promise<string>): Promise<string> => {
  if (typeof navigator === 'undefined' || typeof navigator.locks?.request !== 'function') {
    return fn();
  }

  // Set once the lock is granted and fn has begun; distinguishes a genuine
  // refresh failure (propagate) from a lock-acquisition failure (fall through).
  let fnStarted = false;
  const controller = new AbortController();
  // Manual timer (not AbortSignal.timeout) for older iOS Safari support.
  const acquireTimer = setTimeout(() => controller.abort(), REFRESH_LOCK_ACQUIRE_TIMEOUT_MS);

  try {
    return await navigator.locks.request(
      REFRESH_LOCK_NAME,
      { signal: controller.signal },
      async () => {
        // Lock granted: abort no longer applies, stop the acquire timer.
        clearTimeout(acquireTimer);
        fnStarted = true;
        return fn();
      }
    );
  } catch (error) {
    if (fnStarted) {
      // Genuine refresh failure: let the caller classify / count it.
      throw error;
    }
    // Lock never acquired (timeout AbortError, SecurityError, ...): refresh
    // directly; the per-tab mutex still serializes within this tab.
    devLog.warn('[API] Cross-context refresh lock unavailable, refreshing without it', error);
    return fn();
  } finally {
    clearTimeout(acquireTimer);
  }
};

/**
 * Unified token refresh function
 * Used by both AuthContext and axios interceptor to ensure single source of truth
 * Prevents race conditions by using the same lock
 */
export const refreshAccessToken = async (): Promise<string> => {
  return tokenRefreshLock.refreshWithLock(() => withCrossContextRefreshLock(async () => {
    devLog.log('[API] Attempting to refresh access token...');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REFRESH_TIMEOUT);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/refresh`,
        {},
        {
          withCredentials: true,
          signal: controller.signal,
          timeout: REFRESH_TIMEOUT,
        }
      );

      clearTimeout(timeoutId);

      // Defensive: a 2xx response body without `data.accessToken` used to
      // throw a raw destructuring TypeError here (still "worked" for the
      // breaker below since it's a non-auth failure, but was unclassifiable
      // in logs). Make it explicit.
      const newAccessToken: unknown = response.data?.data?.accessToken;
      if (typeof newAccessToken !== 'string' || newAccessToken.length === 0) {
        throw new TokenRefreshResponseError();
      }

      setAccessToken(newAccessToken);
      devLog.log('[API] Access token refreshed successfully');
      closeRefreshBreaker();

      return newAccessToken;
    } catch (error) {
      clearTimeout(timeoutId);

      let thrownError: unknown = error;

      if (axios.isAxiosError(error) && (error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED')) {
        devLog.error('[API] Token refresh timed out');
        thrownError = new Error('Token refresh timed out. Please try again.');
      }

      // Only a genuine 401/403 from the refresh endpoint means the refresh
      // cookie was actually rejected — that's a definitive, non-retryable
      // outcome (ends in logout via the interceptor) and must NOT count
      // toward the breaker. Everything else (network error, timeout,
      // malformed 2xx body, 5xx) is a transient/non-auth failure.
      if (!isAuthRejection(error)) {
        consecutiveNonAuthRefreshFailures += 1;
        devLog.warn(
          `[API] Non-auth refresh failure (consecutive: ${consecutiveNonAuthRefreshFailures}/${REFRESH_FAILURE_THRESHOLD})`,
          thrownError
        );
        if (consecutiveNonAuthRefreshFailures >= REFRESH_FAILURE_THRESHOLD) {
          openRefreshBreaker();
        }
      }

      throw thrownError;
    }
  }));
};

apiClient.interceptors.response.use(
  (response) => {
    // Gated GET answered with HTTP 200 { locked: true, feature, upsellPlanSlug, data: <empty shape> }:
    // remember the feature + plan so useEntitlement / LockedFeatureCard can show the right upsell.
    if (isLockedResponseBody(response.data)) {
      useGatingStore.getState().recordLocked(response.data.feature, response.data.upsellPlanSlug ?? null);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 409 release.last_reviewer: the mutation would leave a workflow-active
    // brand without a reviewer. Ask once (global dialog), then retry the SAME
    // request with confirmLastReviewer in body AND query, or reject quietly.
    const lastReviewerBody = error.response?.data as
      | { code?: string; brands?: LastReviewerBrand[] }
      | undefined;
    if (
      error.response?.status === 409 &&
      lastReviewerBody?.code === LAST_REVIEWER_ERROR_CODE &&
      originalRequest &&
      !(originalRequest as { _lastReviewerConfirmed?: boolean })._lastReviewerConfirmed
    ) {
      const confirmed = await requestLastReviewerConfirm(lastReviewerBody.brands ?? []);
      if (!confirmed) {
        return Promise.reject(new LastReviewerCancelledError());
      }
      const retryConfig = originalRequest as InternalAxiosRequestConfig & {
        _lastReviewerConfirmed?: boolean;
      };
      retryConfig._lastReviewerConfirmed = true;
      let body: Record<string, unknown> = {};
      if (typeof retryConfig.data === 'string' && retryConfig.data) {
        try {
          const parsed: unknown = JSON.parse(retryConfig.data);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            body = parsed as Record<string, unknown>;
          }
        } catch {
          body = {};
        }
      } else if (retryConfig.data && typeof retryConfig.data === 'object') {
        body = retryConfig.data as Record<string, unknown>;
      }
      retryConfig.data = JSON.stringify({ ...body, confirmLastReviewer: true });
      retryConfig.headers.set('Content-Type', 'application/json');
      retryConfig.params = {
        ...((retryConfig.params as Record<string, unknown> | undefined) ?? {}),
        confirmLastReviewer: true,
      };
      return apiClient(retryConfig);
    }

    // Stale God Mode team (saved currentTeamId fell out of scope): the server answers 404
    // team.not_found. Re-sync /me once (debounced) so the app falls back to the home team /
    // portfolio like it does on focus. Never logs out; skipped for /auth/me itself (no loop).
    const teamCtxBody = error.response?.data as { code?: string } | undefined;
    if (
      error.response?.status === 404 &&
      teamCtxBody?.code === 'team.not_found' &&
      !(originalRequest?.url ?? '').includes('/auth/me')
    ) {
      requestAuthResync();
    }

    // Reviewer-only people may not change brand settings: say so in their language instead of
    // leaving a silent failure (the UI hides these actions; this covers any path that remains).
    const brandSettingsBody = error.response?.data as { code?: string } | undefined;
    if (error.response?.status === 403 && brandSettingsBody?.code === BRAND_SETTINGS_DENIED_CODE) {
      toast.error(i18n.t('errors:release.reviewer_no_brand_settings', { keySeparator: false }));
    }
    if (error.response?.status === 403 && brandSettingsBody?.code === CHANNEL_MANAGE_DENIED_CODE) {
      toast.error(i18n.t('errors:release.reviewer_no_channel_manage', { keySeparator: false }));
    }
    if (error.response?.status === 403 && brandSettingsBody?.code === NOT_CREATOR_CODE) {
      toast.error(i18n.t('errors:release.not_creator', { keySeparator: false }));
    }

    // Plan gating. 403 FEATURE_NOT_AVAILABLE -> ONE upsell modal per feature and session, and only in
    // 'enforce' (legacy-gated keys such as wordpress also answer it in off/shadow: keep their own UI there).
    // 403 INSUFFICIENT_AI_TOKENS -> the token modal on paths that had no handling of their own.
    interface GatingErrorFields {
      feature?: string;
      upsellPlanSlug?: string | null;
      quotaKey?: string;
      resource?: string;
      limitType?: string;
      current?: number;
      used?: number;
      limit?: number | null;
      resetsAt?: string;
      platform?: string;
    }
    const gatingBody = error.response?.data as
      | ({ code?: string; details?: GatingErrorFields } & GatingErrorFields)
      | undefined;
    // Quota / platform limits are the direct result of a user action, so the upsell opens every time
    // (the backend only answers these 403s when the limit is enforced).
    if (error.response?.status === 403 && gatingBody?.code === QUOTA_EXCEEDED_CODE) {
      const quotaKey = gatingBody.quotaKey ?? gatingBody.details?.quotaKey;
      if (quotaKey) {
        openUpsellMarked(error, 
          {
            feature: `quota:${quotaKey}`,
            variant: 'quota',
            upsellPlanSlug: gatingBody.upsellPlanSlug ?? gatingBody.details?.upsellPlanSlug ?? null,
            quota: {
              quotaKey,
              used: gatingBody.used ?? gatingBody.details?.used ?? null,
              limit: gatingBody.limit ?? gatingBody.details?.limit ?? null,
              resetsAt: gatingBody.resetsAt ?? gatingBody.details?.resetsAt ?? null,
            },
          },
          true
        );
      }
    }
    // Fixed package limits (LIMIT_EXCEEDED + resource/current/limit) reuse the same upsell card; no reset date.
    if (error.response?.status === 403 && gatingBody?.code === LIMIT_EXCEEDED_CODE) {
      const resource = gatingBody.resource ?? gatingBody.details?.resource;
      const limitType = gatingBody.limitType ?? gatingBody.details?.limitType;
      if (resource === BRAND_PROFILES_LIMIT_RESOURCE || limitType === BRAND_PROFILES_LIMIT_RESOURCE) {
        openUpsellMarked(error, 
          {
            feature: `quota:${BRAND_PROFILES_LIMIT_RESOURCE}`,
            variant: 'quota',
            upsellPlanSlug: gatingBody.upsellPlanSlug ?? gatingBody.details?.upsellPlanSlug ?? null,
            quota: {
              quotaKey: BRAND_PROFILES_LIMIT_RESOURCE,
              used: gatingBody.current ?? gatingBody.details?.current ?? gatingBody.used ?? gatingBody.details?.used ?? null,
              limit: gatingBody.limit ?? gatingBody.details?.limit ?? null,
              resetsAt: null,
              fixedLimit: true,
            },
          },
          true
        );
      }
    }
    if (error.response?.status === 403 && gatingBody?.code === PLATFORM_NOT_AVAILABLE_CODE) {
      const platform = gatingBody.platform ?? gatingBody.details?.platform;
      if (platform) {
        openUpsellMarked(error, 
          {
            feature: `platform:${platform}`,
            variant: 'platform',
            platform,
            upsellPlanSlug: gatingBody.upsellPlanSlug ?? gatingBody.details?.upsellPlanSlug ?? null,
          },
          true
        );
      }
    }
    if (error.response?.status === 403 && gatingBody?.code === FEATURE_NOT_AVAILABLE_CODE) {
      const feature = gatingBody.feature ?? gatingBody.details?.feature;
      const slug = gatingBody.upsellPlanSlug ?? gatingBody.details?.upsellPlanSlug ?? null;
      if (feature) {
        useGatingStore.getState().recordLocked(feature, slug);
        if (PORTFOLIO_FEATURE_PATTERN.test(feature)) {
          // Brand Portfolio / release keys are decided on the AGENCY package, not the team store mode (staff may
          // have no team). The backend only answers these 403s when enforcing. Reads stay silent (the page shows
          // the locked state itself); only a user action (write) opens the upsell.
          if ((originalRequest?.method ?? 'get').toLowerCase() !== 'get') {
            openUpsellMarked(error, { feature, upsellPlanSlug: slug });
          }
        } else if (
          useGatingStore.getState().mode === 'enforce' &&
          (originalRequest?.method ?? 'get').toLowerCase() !== 'get'
        ) {
          // Same rule: a background read (e.g. the Canva channel list on the Dashboard) must never pop a modal
          // uninvited and is not marked (the caller may toast). Locked entry points open the upsell on click.
          openUpsellMarked(error, { feature, upsellPlanSlug: slug });
        }
      }
    }
    if (
      error.response?.status === 403 &&
      gatingBody?.code === INSUFFICIENT_AI_TOKENS_CODE &&
      TOKEN_MODAL_URL_PATTERNS.some((pattern) => pattern.test(originalRequest?.url ?? ''))
    ) {
      if (useGatingStore.getState().openTokensModal()) markGatingHandled(error);
    }

    // Handle 401 errors (token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry login/register/refresh endpoints
      if (
        originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/register') ||
        originalRequest.url?.includes('/auth/magic/') ||
        originalRequest.url?.includes('/auth/refresh')
      ) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // Circuit breaker: after repeated non-auth refresh failures, don't
      // immediately fire another refresh — fail fast until the cooldown
      // elapses. Without this, every 401 (from every in-flight/retried
      // query) independently calls refreshAccessToken(), which is how a
      // single flaky refresh response turns into an unbounded, silent
      // mid-session loop (Bug B). This does NOT change genuine 401/403
      // auth-rejection semantics — those still flow through refreshAccessToken
      // normally once the breaker is closed/probing again.
      if (isRefreshBreakerOpen()) {
        devLog.warn('[API] Refresh circuit breaker is OPEN — short-circuiting 401 without calling /auth/refresh');
        return Promise.reject(new RefreshBreakerOpenError());
      }

      try {
        // Use the unified refresh function to ensure single source of truth
        const newAccessToken = await refreshAccessToken();

        // Update the failed request with the new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Only treat this as a genuine logout if the refresh endpoint actually
        // responded 401/403 (refresh cookie rejected/expired). A network error
        // or timeout means we simply couldn't verify the session right now —
        // the refresh cookie may still be valid, so we must NOT clear the
        // session or bounce the user to /login. Let the caller's own retry
        // (e.g. React Query) handle it; a later refresh attempt will succeed
        // once connectivity returns.
        if (!isAuthRejection(refreshError)) {
          devLog.warn('[API] Token refresh failed due to connectivity issue, preserving session:', refreshError);
          return Promise.reject(refreshError);
        }

        devLog.error('[API] Token refresh rejected by server (session invalid):', refreshError);
        clearAccessToken();

        // Redirect to login ONLY if not on public pages (auth pages or genuinely public routes)
        const currentPath = window.location.pathname;
        const isPublicPage =
          currentPath === '/login' ||
          currentPath === '/register' ||
          currentPath === '/forgot-password' ||
          currentPath.startsWith('/auth/reset-password') ||
          currentPath === '/auth/magic' ||
          currentPath.startsWith('/Invitation/accept') ||
          currentPath === '/status' ||
          currentPath === '/design-system';

        if (!isPublicPage) {
          // A hard navigation cannot carry React Router location.state. Preserve
          // the intended in-app page (for example a Shop link in an email).
          const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
          window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
        }

        return Promise.reject(refreshError);
      }
    }

    // Handle 502 Bad Gateway errors on safe (idempotent) methods only.
    // Retries GET/HEAD/OPTIONS up to 2 times with backoff so that a brief
    // deploy window or nginx blip shows the user a slow page, not a broken one.
    // POST/PUT/PATCH/DELETE are deliberately excluded to prevent duplicate writes.
    {
      type Retry502Config = InternalAxiosRequestConfig & { _retry502Count?: number };
      const r502 = error.config as Retry502Config | undefined;
      const method = r502?.method?.toLowerCase() ?? '';
      const safeMethods = ['get', 'head', 'options'];

      if (
        error.response?.status === 502 &&
        r502 &&
        safeMethods.includes(method)
      ) {
        r502._retry502Count = (r502._retry502Count ?? 0) + 1;

        if (r502._retry502Count <= 2) {
          const delayMs = r502._retry502Count === 1 ? 1000 : 3000;
          devLog.warn(
            `[API] 502 retry ${r502._retry502Count}/2 for ${r502.method?.toUpperCase()} ${r502.url} after ${delayMs}ms`
          );
          await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
          return apiClient(r502);
        }

        devLog.error(
          `[API] 502 retries exhausted for ${r502.method?.toUpperCase()} ${r502.url}`
        );
      }
    }

    // Handle 409 Conflict errors (optimistic locking failures)
    if (error.response?.status === 409) {
      // Enhance error message for user-friendly display
      interface ConflictErrorResponse {
        error?: { message?: string };
      }
      const errorData = error.response.data as ConflictErrorResponse;
      if (errorData?.error?.message) {
        // Add custom properties to indicate this is an optimistic locking error
        // Using Object.assign to add properties without TS errors
        Object.assign(error, {
          isOptimisticLockError: true,
          userMessage: errorData.error.message,
        });
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Format API errors
 */
export const formatApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiError & { error?: string | { message?: string } }>;

    if (axiosError.response?.data) {
      // Backend returns { success: false, error: "message", code: "ERROR_CODE" }
      // Defensive: `data.error` may be a string OR an object with a `.message` field.
      const data = axiosError.response.data;
      let message: string;
      if (typeof data.message === 'string' && data.message) {
        message = data.message;
      } else if (typeof data.error === 'string' && data.error) {
        message = data.error;
      } else if (data.error && typeof data.error === 'object' && typeof data.error.message === 'string') {
        message = data.error.message;
      } else {
        message = 'An error occurred';
      }
      return {
        message,
        statusCode: axiosError.response.status,
        code: data.code,
        errors: data.errors,
      };
    }

    if (axiosError.code === 'ECONNABORTED') {
      return {
        message: 'The request is taking longer than expected. It may still complete in the background — please check back in a moment before retrying.',
        statusCode: 0,
        code: 'TIMEOUT',
      };
    }

    if (axiosError.request) {
      return {
        message: 'Unable to connect to server. Please check your internet connection and try again.',
        statusCode: 0,
        code: 'NETWORK_ERROR',
      };
    }
  }

  return {
    message: 'An unexpected error occurred. Please try again later.',
    statusCode: 500,
    code: 'UNKNOWN_ERROR',
  };
};

// Export both default and named for flexibility
/**
 * True when the response interceptor already opened a global gating surface for this error (quota/limit upsell,
 * platform upsell, feature upsell, or the AI tokens modal). Callers must skip their own error toast/inline message
 * for it, so the user sees exactly one explanation.
 */
export const isGlobalGatingHandledError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as GatingHandledMarker)[GATING_HANDLED_KEY] === true;

/** Alias kept for existing callers: now covers every global gating surface, not just quota upsells. */
export const isUpsellHandledError = (error: unknown): boolean => isGlobalGatingHandledError(error);

/** True for a 403 PLATFORM_NOT_AVAILABLE answer (the global upsell modal already explains it). */
export const isPlatformNotAvailableError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response?.status === 403 &&
  (error.response.data as { code?: string } | undefined)?.code === PLATFORM_NOT_AVAILABLE_CODE;

export { apiClient };
export default apiClient;
